import { Badge } from "design-system/atoms/Badge"
import { Separator } from "design-system/atoms/Separator"
import { Skeleton } from "design-system/atoms/Skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { formatDate, formatNumber, formatPercent } from "lopermut"
import { TrendingDown, TrendingUp } from "lucide-react"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { AnomalyTypeEnum, InsightSignalSchema } from "schema"
import { z } from "zod"
import { SEVERITY_ORDER } from "."
import { SeverityBadge } from "./SeverityBadge"
import { SolveSignalsButton } from "./SolveSignalsButton"

type SignalItem = z.infer<typeof InsightSignalSchema>

type Props =
  | {
      signal: SignalItem
      isPending: false
      isSingle?: boolean
      groupedSignals?: SignalItem[]
      groupedDates?: string
      showImpact?: boolean
      showAnomalyType?: boolean
    }
  | {
      isPending: true
      signal?: unknown
    }

export function SignalCard({ signal, isPending, ...props }: Props) {
  const { t } = useTranslation()

  const groupedSignals = "groupedSignals" in props ? props.groupedSignals : undefined
  const groupedDates = "groupedDates" in props ? props.groupedDates : undefined
  const showImpact = "showImpact" in props ? props.showImpact : false
  const showAnomalyType = "showAnomalyType" in props ? props.showAnomalyType : false
  const isSingle = "isSingle" in props ? props.isSingle : false

  const isGrouped = groupedSignals !== undefined && groupedSignals.length > 0

  const isPositive = !isPending && signal ? signal.impact === "Positive" : false
  const isSpike = !isPending && signal ? signal.anomalyType === AnomalyTypeEnum.Spike : false
  const Icon = isSpike ? TrendingUp : TrendingDown
  const pulseTooltip = isPositive
    ? t("dashboard.signals.impact.positive")
    : t("dashboard.signals.impact.negative")
  const anomalyTooltip = isSpike
    ? t("dashboard.signals.anomaly.type.spike")
    : t("dashboard.signals.anomaly.type.drop")

  const percentageChange = useMemo(() => {
    if (isPending || !signal) {
      return null
    }

    if (
      signal.baselineValue === null ||
      signal.currentValue === null ||
      signal.baselineValue === 0
    ) {
      return null
    }

    return ((signal.currentValue - signal.baselineValue) / signal.baselineValue) * 100
  }, [isPending, signal])

  const percentageChangeIsPositive = percentageChange !== null && percentageChange > 0

  const maxSeverity = useMemo(() => {
    if (isPending || !signal) {
      return "Low" as const
    }

    if (!isGrouped || !groupedSignals) {
      return signal.severity
    }

    return (
      groupedSignals.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])[0]
        ?.severity ?? signal.severity
    )
  }, [isPending, isGrouped, groupedSignals, signal])

  const allSignalIds = useMemo(() => {
    if (isPending || !signal) {
      return []
    }

    if (!isGrouped || !groupedSignals) {
      return [signal.id]
    }
    return groupedSignals.map((s) => s.id)
  }, [isPending, isGrouped, groupedSignals, signal])

  const allSolved = useMemo(() => {
    if (isPending || !signal) {
      return false
    }

    if (!isGrouped || !groupedSignals) {
      return signal.isSolved
    }
    return groupedSignals.every((s) => s.isSolved)
  }, [isPending, isGrouped, groupedSignals, signal])

  const displayDates = useMemo(() => {
    if (isPending || !signal) {
      return ""
    }

    if (isGrouped && groupedDates) {
      return groupedDates
    }
    return formatDate(signal.detectedAt)
  }, [isPending, isGrouped, groupedDates, signal])

  const content = useMemo(() => {
    if (isPending) {
      const randomWidth1 = Math.floor(Math.random() * 80) + 100
      const randomWidth2 = Math.floor(Math.random() * 200) + 100
      const randomWidth3 = Math.floor(Math.random() * 60) + 100

      return (
        <div className="flex flex-col gap-2 w-full">
          <Skeleton className="h-4" style={{ width: `${randomWidth1}px` }} />
          <Skeleton className="h-4" style={{ width: `${randomWidth2}px` }} />
          <Skeleton className="h-4" style={{ width: `${randomWidth3}px` }} />
        </div>
      )
    }

    const percentage = percentageChange !== null && (
      <Tooltip>
        <TooltipTrigger>
          <span className="text-xs text-left font-medium font-mono text-foreground/90">
            {percentageChangeIsPositive ? "+" : "-"}
            {formatPercent({ amount: Math.abs(percentageChange) / 100, fix: 1 }, true)}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col">
            <div className="text-xs">
              <span>
                {t("dashboard.signals.card.baseline")}
                {": "}
              </span>
              <span className="font-mono">
                {formatNumber({
                  amount: signal.baselineValue ?? undefined,
                  fix: 2,
                })}
              </span>
            </div>
            <div className="text-xs">
              <span>
                {t("dashboard.signals.card.current")}
                {": "}
              </span>
              <span className="font-mono">
                {formatNumber({
                  amount: signal.currentValue ?? undefined,
                  fix: 2,
                })}
              </span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    )

    return (
      <div className="flex flex-col flex-1 gap-1">
        <div className={cn("flex flex-row items-center gap-2", isSingle && "flex-col items-start")}>
          <div className="flex flex-row items-center gap-2">
            {showImpact && (
              <Tooltip>
                <TooltipTrigger>
                  <Pulse
                    className={cn("size-3.5", isPositive ? "fill-positive" : "fill-destructive")}
                  />
                </TooltipTrigger>
                <TooltipContent>{pulseTooltip}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger slick className="shrink-0 text-left">
                <span className="text-sm font-medium text-foreground">{signal.label}</span>
              </TooltipTrigger>
              <TooltipContent>{signal.description}</TooltipContent>
            </Tooltip>
            {!isSingle && <Separator orientation="vertical" />}
            {!isSingle && percentage}
          </div>
          {!isSingle && <Separator orientation="vertical" />}
          <div className="flex flex-row items-center gap-2">
            <span className="text-xs font-normal text-muted-foreground">
              {t("dashboard.signals.detectedOn", { dates: displayDates })}
            </span>
            {isSingle && <Separator orientation="vertical" />}
            {isSingle && percentage}
          </div>
        </div>
      </div>
    )
  }, [
    isPending,
    signal,
    percentageChange,
    percentageChangeIsPositive,
    displayDates,
    t,
    showImpact,
    isPositive,
    pulseTooltip,
    isSingle,
  ])

  return (
    <div
      className={cn(
        "h-full bg-background text-left flex md:flex-row flex-col items-start gap-1 rounded-lg border pl-4 pr-2 py-2 text-sm transition-colors overflow-hidden",
        isSingle && "px-3 pt-3 pb-2 bg-muted/50",
      )}
    >
      <div className="flex md:flex-row flex-col w-full justify-between md:items-center items-start flex-1 gap-1">
        {content}
        {!isPending && (
          <div className="flex flex-row items-center gap-2">
            {showAnomalyType && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    variant="outline"
                    className="size-7 items-center justify-center p-0 shrink-0 text-xs"
                  >
                    <Icon className="size-4" />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{anomalyTooltip}</TooltipContent>
              </Tooltip>
            )}
            <SeverityBadge severity={maxSeverity} noLabel={!isSingle} />
            <SolveSignalsButton signalIds={allSignalIds} isSolved={allSolved} />
          </div>
        )}
      </div>
    </div>
  )
}
